import type { Content } from '@ilteducation/content-registry-types';
import type { BasicCollection } from '@ilteducation/content-collections-types';

export type AggregatedContentCollection = BasicCollection & {
  groupByOriginal?: boolean;
  isRelatedContentCollection?: boolean;
};

export type ContentWithDuplicates = Content & {
  duplicatesCount?: number;
};

export const groupContentByOriginalTitle = (
  contents: Content[],
  sortCallback?: (a: Content, b: Content) => number,
): ContentWithDuplicates[] => {
  // Build buckets - group content by originalTitle
  const buckets: { [key: string]: Content[] } = {};

  for (const content of contents) {
    if (!('originalTitle' in content) || !content.originalTitle) {
      continue;
    }
    const key = content.originalTitle;
    if (!buckets[key]) {
      buckets[key] = [];
    }
    buckets[key].push(content);
  }

  // Sort each bucket so the best representative is first
  if (sortCallback) {
    for (const key in buckets) {
      buckets[key].sort(sortCallback);
    }
  }

  // Build results - iterate original array to preserve order
  const emitted = new Set<string>();
  const results: ContentWithDuplicates[] = [];

  for (const content of contents) {
    if (!('originalTitle' in content) || !content.originalTitle) {
      results.push({ ...content, duplicatesCount: 0 });
      continue;
    }

    const key = content.originalTitle;
    if (emitted.has(key)) continue;

    emitted.add(key);
    const bucket = buckets[key];
    results.push({
      ...bucket[0],
      duplicatesCount: bucket.length,
    });
  }

  return results;
};

export const hasDuplicates = (content: Content): content is ContentWithDuplicates =>
  (content as ContentWithDuplicates).duplicatesCount !== undefined;
